const quickViewState = {
  currentImages: [],
  currentImageIndex: 0,
  productCards: [],
  currentProductIndex: 0,
  selectedVariantId: null,
  scrollLock: false,
  imageLock: false,
  lightboxOpen: false,
  touchStartX: 0,
  touchStartY: 0
};

function getQuickViewModal() {
  return document.getElementById('quick-view-modal');
}

function getQuickViewContainer() {
  return getQuickViewModal()?.querySelector('.quickview-container');
}

function parseProductData(productData) {
  try {
    return JSON.parse(productData);
  } catch (error) {
    console.error('Quick view product JSON is invalid.', error);
    return null;
  }
}

function formatMoney(cents) {
  const amount = Number(cents || 0) / 100;

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  } catch (error) {
    return `$${amount.toFixed(2)}`;
  }
}

function animateElement(element, keyframes, options) {
  if (!element || typeof element.animate !== 'function') {
    return Promise.resolve();
  }

  return element.animate(keyframes, options).finished.catch(() => {});
}

function waitForImageReady(image) {
  if (!image) return Promise.resolve();
  if (image.complete && image.naturalWidth > 0) return Promise.resolve();

  return new Promise((resolve) => {
    const cleanup = () => {
      image.removeEventListener('load', onDone);
      image.removeEventListener('error', onDone);
    };

    const onDone = () => {
      cleanup();
      resolve();
    };

    image.addEventListener('load', onDone, { once: true });
    image.addEventListener('error', onDone, { once: true });

    window.setTimeout(() => {
      cleanup();
      resolve();
    }, 500);
  });
}

function normalizeImageSrc(src) {
  if (!src) return '';
  return src.startsWith('//') ? `https:${src}` : src;
}

function getProductPrimaryImageSrc(product) {
  return normalizeImageSrc(
    product?.featured_image?.src
      || product?.featured_image
      || product?.images?.[0]
      || ''
  );
}

function preloadImage(src) {
  const normalizedSrc = normalizeImageSrc(src);

  if (!normalizedSrc) return Promise.resolve();

  const image = new Image();
  image.decoding = 'async';
  image.src = normalizedSrc;

  return waitForImageReady(image);
}

function preloadImages(sources = []) {
  return Promise.all(
    [...new Set(sources.filter(Boolean).map(normalizeImageSrc))].map((source) => preloadImage(source))
  );
}

function getQuickViewLightbox() {
  return document.querySelector('#quick-view-modal .quickview-lightbox');
}

function syncQuickViewLightbox() {
  const lightbox = getQuickViewLightbox();
  const lightboxImage = lightbox?.querySelector('.quickview-lightbox__image');
  const lightboxCount = lightbox?.querySelector('.quickview-lightbox__count');

  if (!lightbox || !lightboxImage || !quickViewState.currentImages.length) return;

  lightboxImage.src = quickViewState.currentImages[quickViewState.currentImageIndex] || '';
  lightboxCount.textContent = `${quickViewState.currentImageIndex + 1} / ${quickViewState.currentImages.length}`;
}

function openQuickViewLightbox() {
  const lightbox = getQuickViewLightbox();

  if (!lightbox || !quickViewState.currentImages.length) return;

  quickViewState.lightboxOpen = true;
  lightbox.classList.add('is-open');
  lightbox.setAttribute('aria-hidden', 'false');
  syncQuickViewLightbox();
}

function closeQuickViewLightbox() {
  const lightbox = getQuickViewLightbox();

  if (!lightbox) return;

  quickViewState.lightboxOpen = false;
  lightbox.classList.remove('is-open');
  lightbox.setAttribute('aria-hidden', 'true');
}

function stepQuickViewLightbox(step) {
  if (!quickViewState.currentImages.length) return;

  quickViewState.currentImageIndex =
    (quickViewState.currentImageIndex + step + quickViewState.currentImages.length) % quickViewState.currentImages.length;

  syncQuickViewLightbox();
}

function renderImageDots() {
  const dotsWrap = document.querySelector('#quick-view-modal .quickview-dots');

  if (!dotsWrap) return;

  if (quickViewState.currentImages.length <= 1) {
    dotsWrap.innerHTML = '';
    return;
  }

  dotsWrap.innerHTML = quickViewState.currentImages
    .map((_, index) => {
      const isActive = index === quickViewState.currentImageIndex ? ' is-active' : '';
      return `<button type="button" class="quickview-dot${isActive}" data-image-dot-index="${index}" aria-label="Go to image ${index + 1}"></button>`;
    })
    .join('');
}

function goToImage(index) {
  if (!quickViewState.currentImages.length || index === quickViewState.currentImageIndex) return;

  const stepDirection = index > quickViewState.currentImageIndex ? 'next' : 'prev';
  quickViewState.currentImageIndex = index;

  transitionFeaturedImage(
    quickViewState.currentImages[quickViewState.currentImageIndex],
    stepDirection,
    'x'
  );

  renderImageDots();
  if (quickViewState.lightboxOpen) syncQuickViewLightbox();
}

function getImageOffset(direction, axis) {
  const amount = axis === 'x'
    ? Math.max(Math.round(window.innerWidth * 0.7), 380)
    : Math.max(Math.round(window.innerHeight * 0.45), 240);

  if (axis === 'x') {
    return direction === 'next' ? [-amount, 0] : [amount, 0];
  }

  return direction === 'next' ? [0, -amount] : [0, amount];
}

async function transitionFeaturedImage(nextSrc, direction = 'next', axis = 'x', immediate = false) {
  const img = document.querySelector('#quick-view-modal .quickview-featured img');
  const imageStage = img?.parentElement;

  if (!img || !nextSrc) return;
  if (quickViewState.imageLock && !immediate) return;
  if (immediate || img.src === nextSrc || typeof img.animate !== 'function') {
    img.src = nextSrc;
    img.style.opacity = '1';
    img.style.transform = 'translate3d(0, 0, 0) scale(1)';
    renderImageDots();
    return;
  }

  quickViewState.imageLock = true;
  const [offsetX, offsetY] = getImageOffset(direction, axis);
  const incomingImage = img.cloneNode(true);

  incomingImage.src = nextSrc;
  incomingImage.setAttribute('aria-hidden', 'true');
  incomingImage.style.position = 'absolute';
  incomingImage.style.inset = '0';
  incomingImage.style.margin = 'auto';
  incomingImage.style.pointerEvents = 'none';
  incomingImage.style.opacity = '0';
  incomingImage.style.transform = `translate3d(${-offsetX}px, ${-offsetY}px, 0) scale(0.94)`;

  imageStage?.appendChild(incomingImage);
  await waitForImageReady(incomingImage);

  const outgoingAnimation = img.animate(
    [
      { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
      { opacity: 0.18, transform: `translate3d(${offsetX}px, ${offsetY}px, 0) scale(0.94)` }
    ],
    {
      duration: 220,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'forwards'
    }
  );

  const incomingAnimation = incomingImage.animate(
    [
      { opacity: 0.15, transform: `translate3d(${-offsetX}px, ${-offsetY}px, 0) scale(0.94)` },
      { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' }
    ],
    {
      duration: 280,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'forwards'
    }
  );

  try {
    await Promise.all([
      outgoingAnimation.finished.catch(() => {}),
      incomingAnimation.finished.catch(() => {})
    ]);

    img.src = nextSrc;
    await waitForImageReady(img);
  } finally {
    outgoingAnimation.cancel();
    incomingAnimation.cancel();
    img.style.opacity = '1';
    img.style.transform = 'translate3d(0, 0, 0) scale(1)';
    incomingImage.remove();
    quickViewState.imageLock = false;
    renderImageDots();
  }
}

function setQuickViewProductData(productData) {
  const container = getQuickViewContainer();

  if (container) {
    container.dataset.product = productData;
  }
}

function renderImages(product, options = {}) {
  const currentImages = (product.images || []).map((image) => {
    return normalizeImageSrc(image);
  });

  quickViewState.currentImages = currentImages;

  let featuredSrc = getProductPrimaryImageSrc(product) || currentImages[0] || '';

  const featuredIndex = currentImages.indexOf(featuredSrc);
  quickViewState.currentImageIndex = featuredIndex >= 0 ? featuredIndex : 0;

  transitionFeaturedImage(
    quickViewState.currentImages[quickViewState.currentImageIndex],
    'next',
    'x',
    options.immediate !== false
  );
  renderImageDots();

  // Warm nearby images early so the first manual swipe does not stall on network fetch.
  const preloadTargets = currentImages.slice(0, 4);
  preloadImages(preloadTargets).catch(() => {});
}

function renderDescription(product) {
  const description = document.querySelector('#quick-view-modal .quick-description');
  const toggle = document.querySelector('#quick-view-modal .quick-description-toggle');

  if (!description || !toggle) return;

  let text = product.description || '';

  text = text.replace(/<br\s*\/?>/gi, ' ');
  text = text.replace(/<[^>]*>/g, '');
  text = text.replace(/\s+/g, ' ').trim();

  const shortText = text.length > 180 ? `${text.substring(0, 180).trim()}...` : text;
  const isExpandable = text.length > 180;

  description.dataset.fullText = text;
  description.dataset.shortText = shortText;
  description.dataset.expanded = 'false';
  description.classList.toggle('is-collapsed', isExpandable);
  description.innerText = isExpandable ? shortText : text;

  toggle.classList.toggle('is-visible', isExpandable);
  toggle.innerText = 'Read more';
}

function renderVariants(product) {
  const wrap = document.querySelector('#quick-view-modal .quick-variants');

  if (!wrap) return;

  wrap.innerHTML = '';

  if (!product.variants || product.variants.length <= 1) return;

  product.variants.forEach((variant, index) => {
    const button = document.createElement('button');
    button.className = 'quick-variant';
    button.type = 'button';
    button.dataset.id = variant.id;
    button.dataset.available = variant.available ? 'true' : 'false';

    if (variant.featured_image?.src) {
      button.dataset.image = variant.featured_image.src.startsWith('//')
        ? `https:${variant.featured_image.src}`
        : variant.featured_image.src;
    }

    button.textContent = variant.title;

  if (variant.available && quickViewState.selectedVariantId === null) {
    quickViewState.selectedVariantId = variant.id;
  }

    wrap.appendChild(button);
  });
}

function updateQuickAddState(product) {
  const quickAddButton = document.querySelector('#quick-view-modal .quick-add');

  if (!quickAddButton) return;
  if (quickAddButton.dataset.busy === 'true') return;

  const selectedVariant = product?.variants?.find(
    (variant) => Number(variant.id) === Number(quickViewState.selectedVariantId)
  );

  const isAvailable = Boolean(selectedVariant?.available);
  const label = quickAddButton.querySelector('.quick-add__label');

  quickAddButton.disabled = !isAvailable;
  if (label) {
    label.textContent = isAvailable ? 'Add To Cart' : 'Sold out';
  } else {
    quickAddButton.textContent = isAvailable ? 'Add To Cart' : 'Sold out';
  }
}

function setQuickAddLoading(button, isLoading) {
  if (!button) return;

  const label = button.querySelector('.quick-add__label');

  button.dataset.busy = isLoading ? 'true' : 'false';
  button.disabled = isLoading;
  button.classList.toggle('is-loading', isLoading);
  button.setAttribute('aria-busy', isLoading ? 'true' : 'false');

  if (label) {
    label.textContent = isLoading ? 'Adding...' : 'Add To Cart';
  } else {
    button.textContent = isLoading ? 'Adding...' : 'Add To Cart';
  }
}

function setDefaultVariant(product) {
  const defaultVariant = product.variants?.find((variant) => variant.available) || product.variants?.[0];

  quickViewState.selectedVariantId = defaultVariant?.id || null;

  document.querySelectorAll('#quick-view-modal .quick-variant').forEach((variantButton) => {
    const isAvailable = variantButton.dataset.available === 'true';
    const isSelected = Number(variantButton.dataset.id) === Number(quickViewState.selectedVariantId);

    variantButton.disabled = !isAvailable;
    variantButton.classList.toggle('active', isAvailable && isSelected);
  });

  updateQuickAddState(product);
}


function animateQuickViewPanels(direction = 'next', phase = 'in') {
  const directionMultiplier = direction === 'next' ? 1 : -1;
  const slider = document.querySelector('#quick-view-modal .quickview-slider');
  const dots = document.querySelector('#quick-view-modal .quickview-dots');
  const info = document.querySelector('#quick-view-modal .quickview-info');
  const travel = Math.max(Math.round(window.innerHeight * 0.3), 140);

  return Promise.all([slider, dots, info].filter(Boolean).map((element, index) => {
    const distance = directionMultiplier * (
      index === 0 ? travel : index === 1 ? Math.round(travel * 0.7) : Math.round(travel * 0.5)
    );
    const keyframes = phase === 'out'
      ? [
          { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
          { opacity: 0, transform: `translate3d(0, ${distance}px, 0) scale(0.98)` }
        ]
      : [
          { opacity: 0, transform: `translate3d(0, ${-distance}px, 0) scale(0.98)` },
          { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' }
        ];

    return animateElement(
      element,
      keyframes,
      {
        duration: phase === 'out' ? 140 : 220,
        delay: phase === 'out' ? 0 : index * 18,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        fill: 'both'
      }
    );
  }));
}

async function transitionQuickViewProduct(product, direction = 'next') {
  await preloadImage(getProductPrimaryImageSrc(product));
  await animateQuickViewPanels(direction, 'out');
  renderQuickView(product, { immediateImages: true, animateProduct: false });
  await animateQuickViewPanels(direction, 'in');
}

function renderQuickView(product, options = {}) {
  const modal = getQuickViewModal();

  if (!modal) return;

  const title = modal.querySelector('h3');
  const price = modal.querySelector('p');

  if (title) title.innerText = product.title;
  if (price) price.innerText = formatMoney(product.price);

  renderImages(product, { immediate: options.immediateImages !== false });
  renderVariants(product);
  renderDescription(product);
  setDefaultVariant(product);

  if (options.animateProduct) {
    animateQuickViewPanels(options.direction);
  }
}

function updateCartCount() {
  fetch('/cart.js')
    .then((response) => response.json())
    .then((cart) => {
      const cartIcon = document.querySelector('#quick-view-modal .quick-cart-icon #cart-icon-bubble');

      if (!cartIcon) return;

      let bubble = cartIcon.querySelector('.cart-count-bubble');

      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = '<span aria-hidden="true">0</span><span class="visually-hidden">0 items</span>';
        cartIcon.appendChild(bubble);
      }

      const visibleCount = bubble.querySelector('span[aria-hidden="true"]');
      const srText = bubble.querySelector('.visually-hidden');
      const itemCount = Number(cart.item_count || 0);

      bubble.style.display = itemCount > 0 ? '' : 'none';

      if (visibleCount) visibleCount.textContent = String(itemCount);
      if (srText) srText.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
    })
    .catch(() => {});
}

function openQuickView(productData, entries, multiplier) {
  const modal = getQuickViewModal();
  const product = parseProductData(productData);
  if (!modal || !product) return;

  setQuickViewProductData(productData);
  renderQuickView(product, { immediateImages: true, animateProduct: false });

  modal.style.display = 'block';
  requestAnimationFrame(() => modal.classList.add('is-open'));
  document.body.style.overflow = 'hidden';

  if (modal) {
    const entriesEl = modal.querySelector('.entriesddd');
    const multiplierEl = modal.querySelector('.badgeddd');

    if (entriesEl) entriesEl.textContent = entries || '';
    if (multiplierEl) multiplierEl.textContent = multiplier || '';
  }

  updateCartCount();
}

function closeQuickView() {
  const modal = getQuickViewModal();

  if (!modal) return;

  closeQuickViewLightbox();

  modal.classList.remove('is-open');
  window.setTimeout(() => {
    modal.style.display = 'none';
  }, 180);

  document.body.style.overflow = '';
}

function stepImage(step) {
  if (!quickViewState.currentImages.length) return;

  quickViewState.currentImageIndex =
    (quickViewState.currentImageIndex + step + quickViewState.currentImages.length) % quickViewState.currentImages.length;

  transitionFeaturedImage(
    quickViewState.currentImages[quickViewState.currentImageIndex],
    step > 0 ? 'next' : 'prev',
    'x'
  );
  renderImageDots();
  if (quickViewState.lightboxOpen) syncQuickViewLightbox();

  const nextIndexes = [
    quickViewState.currentImageIndex,
    (quickViewState.currentImageIndex + 1) % quickViewState.currentImages.length,
    (quickViewState.currentImageIndex + 2) % quickViewState.currentImages.length
  ];
  preloadImages(nextIndexes.map((index) => quickViewState.currentImages[index])).catch(() => {});
}

async function stepProduct(step, motionDirection = null) {
  if (quickViewState.scrollLock) return;

  const targetIndex = quickViewState.currentProductIndex + step;

  if (targetIndex < 0 || targetIndex >= quickViewState.productCards.length) return;

  const nextCard = quickViewState.productCards[targetIndex];
  const product = parseProductData(nextCard?.dataset.product || '');

  if (!product) return;

  quickViewState.scrollLock = true;
  quickViewState.currentProductIndex = targetIndex;

  setQuickViewProductData(nextCard.dataset.product);
  await transitionQuickViewProduct(product, motionDirection || (step > 0 ? 'next' : 'prev'));

  const modal = getQuickViewModal();
  if (modal) {
    const entriesEl = modal.querySelector('.entriesddd');
    const multiplierEl = modal.querySelector('.badgeddd');

    if (entriesEl) {
      entriesEl.textContent = nextCard.dataset.productEntries || '';
    }

    if (multiplierEl) {
      multiplierEl.textContent = nextCard.dataset.productMultiplier || '';
    }
  }

  window.setTimeout(() => {
    quickViewState.scrollLock = false;
  }, 80);
}

function addToCart(variantId) {
  const useDawnDrawer = window.themeCartType === 'drawer' && Boolean(document.querySelector('cart-drawer'));
  const sections = useDawnDrawer ? ['cart-drawer', 'cart-icon-bubble'] : ['cart-icon-bubble'];

  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: variantId,
      quantity: 1,
      sections,
      sections_url: window.location.pathname
    })
  }).then((response) => response.json())
  .then((state) => {
    if (!useDawnDrawer && window.themeCartType !== 'drawer') {
      window.location.href = window.routes?.cart_url || '/cart';
      return;
    }

    if (!useDawnDrawer) {
      updateCartCount();
      return;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && state.sections && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(state);
    } else if (cartDrawer) {
      return refreshCartDrawer().then(() => cartDrawer.open());
    }

    updateCartCount();
  });
}

function refreshCartDrawer() {
  const sectionRequests = [
    fetch('/?sections=cart-drawer,cart-icon-bubble').then((response) => response.json())
  ];

  return Promise.all(sectionRequests)
    .then(([sections]) => {
      const cartDrawer = document.querySelector('cart-drawer');
      const drawerHtml = sections?.['cart-drawer'];
      const bubbleHtml = sections?.['cart-icon-bubble'];

      if (cartDrawer && drawerHtml && typeof cartDrawer.getSectionInnerHTML === 'function') {
        const drawerTarget = document.querySelector('#CartDrawer');
        if (drawerTarget) {
          drawerTarget.innerHTML = cartDrawer.getSectionInnerHTML(drawerHtml, '#CartDrawer');
        }
      }

      if (bubbleHtml) {
        document.querySelectorAll('#cart-icon-bubble').forEach((target) => {
          target.innerHTML = new DOMParser()
            .parseFromString(bubbleHtml, 'text/html')
            .querySelector('.shopify-section')?.innerHTML || target.innerHTML;
        });
      }

      updateCartCount();
    });
}

function triggerCartClick() {
  if (window.themeCartType !== 'drawer') {
    window.location.href = window.routes?.cart_url || '/cart';
    return;
  }

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.open === 'function') {
    cartDrawer.open();
    return;
  }

  const cartIcon = document.querySelector('#quick-view-modal #cart-icon-bubble')
    || document.querySelector('#cart-icon-bubble');

  if (cartIcon) {
    if (typeof window.refreshSlideCart === 'function') {
      window.refreshSlideCart({ open: true });
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    cartIcon.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    return;
  }

  window.location.href = window.routes?.cart_url || '/cart';
}

function handleQuickViewSwipe(endX, endY) {
  const modal = getQuickViewModal();

  if (!modal || modal.style.display !== 'block') return;

  const diffX = quickViewState.touchStartX - endX;
  const diffY = quickViewState.touchStartY - endY;

  if (Math.abs(diffX) < 40 && Math.abs(diffY) < 40) return;

  if (Math.abs(diffX) > Math.abs(diffY)) {
    stepImage(diffX > 0 ? 1 : -1);
  } else {
    if (diffY > 0) {
      stepProduct(1, 'prev');
    } else {
      stepProduct(-1, 'next');
    }
  }
}

document.addEventListener('click', (event) => {
  if (event.target.closest('#quick-view-modal .quickview-lightbox__close')) {
    closeQuickViewLightbox();
    return;
  }

  if (event.target.closest('#quick-view-modal .quickview-lightbox__nav--prev')) {
    stepQuickViewLightbox(-1);
    return;
  }

  if (event.target.closest('#quick-view-modal .quickview-lightbox__nav--next')) {
    stepQuickViewLightbox(1);
    return;
  }

  if (
    event.target.classList?.contains('quickview-lightbox')
    || event.target.classList?.contains('quickview-lightbox__image')
  ) {
    closeQuickViewLightbox();
    return;
  }

  const productCard = event.target.closest('.product-card-link');

  if (productCard) {
    const section = productCard.closest('.product-card-section');

    if (!section) return;

    event.preventDefault();

    quickViewState.productCards = [...section.querySelectorAll('.product-card-link')];
    quickViewState.currentProductIndex = quickViewState.productCards.indexOf(productCard);

    openQuickView(
      productCard.dataset.product,
      productCard.dataset.productEntries,
      productCard.dataset.productMultiplier
    );
    return;
  }

  if (event.target.closest('.slider-arrow.next')) {
    stepImage(1);
    return;
  }

  if (event.target.closest('.slider-arrow.prev')) {
    stepImage(-1);
    return;
  }

  const imageDot = event.target.closest('[data-image-dot-index]');
  if (imageDot) {
    goToImage(Number(imageDot.dataset.imageDotIndex));
    return;
  }

  if (event.target.closest('#quick-view-modal .quickview-featured img')) {
    openQuickViewLightbox();
    return;
  }

  if (event.target.closest('.slider-arrow.bottom')) {
    stepProduct(1);
    return;
  }

  if (event.target.closest('.slider-arrow.top')) {
    stepProduct(-1);
    return;
  }

  if (event.target.classList.contains('quick-close')) {
    closeQuickView();
    return;
  }

  if (event.target.id === 'quick-view-modal') {
    closeQuickView();
    return;
  }

  if (event.target.classList.contains('quick-variant')) {
    const variantButton = event.target;
    const product = parseProductData(getQuickViewContainer()?.dataset.product || '');

    document.querySelectorAll('#quick-view-modal .quick-variant').forEach((button) => {
      button.classList.remove('active');
    });

    variantButton.classList.add('active');
    quickViewState.selectedVariantId = Number(variantButton.dataset.id);

    if (variantButton.dataset.image) {
      const imageIndex = quickViewState.currentImages.indexOf(variantButton.dataset.image);

      if (imageIndex >= 0) {
        quickViewState.currentImageIndex = imageIndex;
        transitionFeaturedImage(quickViewState.currentImages[imageIndex], 'next', 'x');
      }
    }

    if (product) {
      updateQuickAddState(product);
    }

    return;
  }

  if (event.target.classList.contains('quick-description-toggle')) {
    const description = document.querySelector('#quick-view-modal .quick-description');
    const toggle = event.target;

    if (!description) return;

    const isExpanded = description.dataset.expanded === 'true';

    description.dataset.expanded = isExpanded ? 'false' : 'true';
    description.classList.toggle('is-collapsed', isExpanded);
    description.innerText = isExpanded
      ? (description.dataset.shortText || '')
      : (description.dataset.fullText || '');
    toggle.innerText = isExpanded ? 'Read more' : 'Show less';
    return;
  }

  if (event.target.classList.contains('product_description')) {
    const toggle = document.querySelector('#quick-view-modal .quick-description-toggle.is-visible');

    if (toggle) {
      toggle.click();
    }
    return;
  }

  const quickAddButton = event.target.closest('.quick-add');
  if (quickAddButton) {

    if (quickAddButton.dataset.busy === 'true') return;

    const product = parseProductData(getQuickViewContainer()?.dataset.product || '');

    if (!product) return;

    const selectedVariant = product.variants?.find(
      v => Number(v.id) === Number(quickViewState.selectedVariantId)
    );

    if (!selectedVariant || !selectedVariant.available) {
      alert('This product is sold out');
      return;
    }

    setQuickAddLoading(quickAddButton, true);

    addToCart(selectedVariant.id)
      .then(() => {
        if (!document.querySelector('cart-drawer')) {
          triggerCartClick();
        }
      })
      .catch((error) => {
        console.error('Quick view add-to-cart failed.', error);
        alert('Could not add this product to cart. Please try again.');
      })
      .finally(() => {
        setQuickAddLoading(quickAddButton, false);
        updateQuickAddState(product);
      });
  }

});

document.addEventListener(
  'wheel',
  (event) => {
    const modal = getQuickViewModal();

    if (!modal || modal.style.display !== 'block') return;

    event.preventDefault();
    stepProduct(event.deltaY > 0 ? 1 : -1);
  },
  { passive: false }
);

document.addEventListener('keydown', (event) => {
  const modal = getQuickViewModal();

  if (!modal || modal.style.display !== 'block') return;

  if (quickViewState.lightboxOpen) {
    if (event.key === 'Escape') {
      closeQuickViewLightbox();
    } else if (event.key === 'ArrowRight') {
      stepQuickViewLightbox(1);
    } else if (event.key === 'ArrowLeft') {
      stepQuickViewLightbox(-1);
    }
    return;
  }

  if (event.key === 'Escape') {
    closeQuickView();
  } else if (event.key === 'ArrowRight') {
    stepImage(1);
  } else if (event.key === 'ArrowLeft') {
    stepImage(-1);
  } else if (event.key === 'ArrowDown') {
    stepProduct(1);
  } else if (event.key === 'ArrowUp') {
    stepProduct(-1);
  }
});

const quickViewModal = getQuickViewModal();

if (quickViewModal) {
  quickViewModal.addEventListener('touchstart', (event) => {
    quickViewState.touchStartX = event.touches[0].clientX;
    quickViewState.touchStartY = event.touches[0].clientY;
  });

  quickViewModal.addEventListener('touchend', (event) => {
    handleQuickViewSwipe(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
  });
}
